import React from 'react'
import { ScrollReveal } from './ScrollReveal'

const rows = [
  {
    task: 'Chat completion (1K calls)',
    openaiPrice: '$1.25',
    openaiNote: 'GPT-4o @ $1.25/M in',
    cmPrice: '$0.35',
    cmNote: 'NS Speed @ Llama 4',
    save: '-72%',
  },
  {
    task: 'Code generation (1K calls)',
    openaiPrice: '$2.00',
    openaiNote: 'GPT-4.1 @ $2/M in',
    cmPrice: '$0.39',
    cmNote: 'NS Coder @ GPT-OSS',
    save: '-81%',
  },
  {
    task: 'Summarization (10K docs)',
    openaiPrice: '$10.00',
    openaiNote: 'GPT-4o Mini @ $0.25/M',
    cmPrice: '$0.62',
    cmNote: 'NS Balanced @ DeepSeek',
    save: '-94%',
  },
  {
    task: 'Frontier reasoning',
    openaiPrice: '$10.00',
    openaiNote: 'GPT-4o @ $10/M out',
    cmPrice: '$6.20',
    cmNote: 'NS Pro @ GPT-4o via OR',
    save: '-38%',
  },
  {
    task: 'Image generation (100 imgs)',
    openaiPrice: '$4.00',
    openaiNote: 'DALL-E ~$0.04/img',
    cmPrice: '$0.14',
    cmNote: 'NS Image @ SDXL',
    save: '-97%',
  },
  {
    task: 'Speech-to-text (60 min)',
    openaiPrice: '$0.36',
    openaiNote: 'Whisper @ $0.006/s',
    cmPrice: '$0.14',
    cmNote: 'NS Transcribe',
    save: '-61%',
  },
]

const highlights = [
  {
    title: 'No USD card',
    desc: 'Pay in PHP, SGD, AUD...',
  },
  {
    title: 'One API key',
    desc: 'Access all model tiers',
  },
  {
    title: 'Same format',
    desc: 'OpenAI-compatible SDK',
  },
]

export function PricingComparison() {
  return (
    <section className="py-24 bg-[#07090f] relative overflow-hidden">
      {/* Ambient glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-indigo-600/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="container relative z-10">
        {/* Header */}
        <ScrollReveal className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-white">
            Why pay OpenAI prices?
          </h2>
          <p className="mt-4 text-lg text-gray-400">
            Same quality models. Fraction of the cost. And we bill in your currency.
          </p>
        </ScrollReveal>

        {/* Table */}
        <ScrollReveal delay={100}>
          <div className="rounded-2xl border border-white/10 overflow-hidden">
            {/* Table header */}
            <div className="grid grid-cols-4 px-6 py-3 bg-white/[0.03] border-b border-white/10">
              <span className="text-xs font-semibold tracking-widest text-gray-500 uppercase">Task</span>
              <span className="text-xs font-semibold tracking-widest text-gray-500 uppercase text-right">OpenAI Direct</span>
              <span className="text-xs font-semibold tracking-widest text-emerald-400 uppercase text-right">Neoserve</span>
              <span className="text-xs font-semibold tracking-widest text-gray-500 uppercase text-right">You Save</span>
            </div>

            {/* Rows */}
            <div className="divide-y divide-white/[0.05]">
              {rows.map((row, i) => (
                <div
                  key={row.task}
                  className="grid grid-cols-4 px-6 py-4 items-center hover:bg-white/[0.02] transition-colors"
                  style={{
                    opacity: 0,
                    animation: `fadeSlideIn 0.4s ease forwards ${150 + i * 60}ms`,
                  }}
                >
                  <span className="text-sm text-gray-200 font-medium">{row.task}</span>

                  <div className="text-right">
                    <p className="text-sm text-gray-500 line-through">{row.openaiPrice}</p>
                    <p className="text-xs text-gray-600 mt-0.5">{row.openaiNote}</p>
                  </div>

                  <div className="text-right">
                    <p className="text-sm font-semibold text-emerald-400">{row.cmPrice}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{row.cmNote}</p>
                  </div>

                  <div className="flex justify-end">
                    <span className="inline-block px-2.5 py-1 rounded-md text-xs font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                      {row.save}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </ScrollReveal>

        {/* Disclaimer */}
        <ScrollReveal delay={150}>
          <p className="mt-4 text-xs text-gray-600 text-center leading-relaxed max-w-3xl mx-auto">
            * Prices are estimates based on publicly listed rates as of Q1 2025 and may vary. Comparisons use representative input/output token volumes.{' '}
            Neoserve pricing is subject to change during Early Access. All prices shown in USD equivalent.
          </p>
        </ScrollReveal>

        {/* Highlight cards */}
        <ScrollReveal delay={200}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
            {highlights.map(h => (
              <div
                key={h.title}
                className="rounded-xl border border-white/10 bg-white/[0.03] px-6 py-4 text-center hover:bg-white/[0.05] transition-colors"
              >
                <p className="text-sm font-semibold text-emerald-400">{h.title}</p>
                <p className="text-xs text-gray-500 mt-1">{h.desc}</p>
              </div>
            ))}
          </div>
        </ScrollReveal>
      </div>

      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  )
}
